// Skins #10-#17: Doomer, Bloomer, Chad, NPC, Anon, Pepe, Shiba, GigaChad builders.

import * as THREE from 'three';
import {
  SEG, darken, lighten, mat, matEmissive, matSkin, enableShadows, buildHumanoid,
  addBuzzCut,
} from './skin-utils';

/* ------------------------------------------------------------------ */
/*  #10 — The Doomer: Black hoodie pale face with cigarette            */
/* ------------------------------------------------------------------ */

export function buildTheDoomer(color: number): THREE.Group {
  const group = new THREE.Group();
  const hoodieColor = 0x111111;
  const skinColor = 0xe8d5c0; // pale

  const body = buildHumanoid({
    bodyColor: hoodieColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.08,
    armColor: hoodieColor,
    legColor: 0x0a0a0a,
    bootColor: 0x0a0a0a,
    handColor: skinColor,
    lean: 0.05,
    eyeColor: 0x556655,
  });
  group.add(body);

  // Hoodie over head — pulled forward
  const hood = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, SEG, SEG, 0, Math.PI * 2, 0, Math.PI * 0.65),
    mat(hoodieColor),
  );
  hood.position.set(0, 1.74, 0.02);
  group.add(hood);
  // Hood edge
  const hoodEdge = new THREE.Mesh(
    new THREE.TorusGeometry(0.15, 0.01, 6, 12, Math.PI),
    mat(darken(hoodieColor, 0.8)),
  );
  hoodEdge.position.set(0, 1.71, -0.07);
  hoodEdge.rotation.x = 0.2;
  group.add(hoodEdge);

  // Dark circles under eyes (override — doomer hasn't slept)
  for (const side of [-0.04, 0.04]) {
    const circle = new THREE.Mesh(new THREE.SphereGeometry(0.016, 6, 6), mat(0x4a3a5a));
    circle.position.set(side, 1.63, -0.125);
    circle.scale.set(1.3, 0.6, 0.5);
    group.add(circle);
  }

  // Sad mouth frown
  const frown = new THREE.Mesh(
    new THREE.TorusGeometry(0.025, 0.004, 4, 8, Math.PI),
    mat(0x8a6a6a),
  );
  frown.position.set(0, 1.54, -0.125);
  // Not flipped = frown
  group.add(frown);

  // Cigarette
  const cig = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.007, 0.09, 8), mat(0xe0d8cc));
  cig.position.set(0.04, 1.54, -0.16);
  cig.rotation.z = 0.3;
  cig.rotation.x = -0.1;
  group.add(cig);
  const tip = new THREE.Mesh(new THREE.SphereGeometry(0.008, 6, 6), matEmissive(0xff6600, 2));
  tip.position.set(0.065, 1.555, -0.18);
  group.add(tip);

  // Hoodie strings
  for (const side of [-0.04, 0.04]) {
    const string = new THREE.Mesh(new THREE.CylinderGeometry(0.003, 0.003, 0.12, 4), mat(0x333333));
    string.position.set(side, 1.40, -0.14);
    group.add(string);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #11 — The Bloomer: Bright optimistic character                     */
/* ------------------------------------------------------------------ */

export function buildTheBloomer(color: number): THREE.Group {
  const group = new THREE.Group();
  const skinColor = 0xf0d0b0;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: skinColor,
    skinColor,
    armColor: lighten(color, 1.08),
    legColor: 0x6688cc,
    bootColor: 0xffffff,
    handColor: skinColor,
    eyeColor: 0x2288ff,
  });
  group.add(body);

  // Bright blonde hair
  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(0.155, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.52),
    mat(0xf0d060),
  );
  hair.position.set(0, 1.74, 0);
  group.add(hair);
  // Side hair puffs
  for (const side of [-1, 1]) {
    const puff = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 6), mat(0xf0d060));
    puff.position.set(side * 0.12, 1.66, 0.06);
    group.add(puff);
  }

  // Big smile (override the neutral mouth)
  const smile = new THREE.Mesh(
    new THREE.TorusGeometry(0.035, 0.006, 6, 10, Math.PI),
    mat(0xcc6666),
  );
  smile.position.set(0, 1.54, -0.125);
  smile.rotation.x = Math.PI;
  group.add(smile);

  // Rosy cheeks
  for (const side of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 6), mat(0xffaaaa));
    cheek.position.set(side * 0.07, 1.60, -0.12);
    cheek.scale.set(1.3, 0.8, 0.4);
    group.add(cheek);
  }

  // Flower/star crypto accents on chest
  for (let i = 0; i < 3; i++) {
    const star = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.022, 0),
      mat(i === 0 ? 0xff6699 : i === 1 ? 0xffcc33 : 0x66ccff),
    );
    star.position.set(-0.05 + i * 0.05, 1.24 - (i % 2) * 0.04, -0.19);
    star.rotation.z = Math.PI / 4;
    group.add(star);
  }

  // Sunflower pin
  const sunflower = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.01, 8),
    matEmissive(0xffcc00, 0.5),
  );
  sunflower.position.set(0.12, 1.35, -0.18);
  sunflower.rotation.x = Math.PI / 2;
  group.add(sunflower);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #12 — The Chad: Exaggerated jawline, confident stance              */
/* ------------------------------------------------------------------ */

export function buildTheChad(color: number): THREE.Group {
  const group = new THREE.Group();
  const skinColor = 0xe0c0a0;

  const body = buildHumanoid({
    bodyColor: 0xcc3333,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.4,
    armColor: 0xcc3333,
    legColor: 0x2a2a55,
    bootColor: 0x1a1a1a,
    handColor: skinColor,
    shoulderWidth: 0.32,
    legSpread: 0.02,
    muscular: true,
    eyeColor: 0x3366aa,
  });
  group.add(body);

  // OVERSIZED SQUARE JAW — signature (bigger than humanoid default jaw)
  const bigJaw = new THREE.Mesh(
    new THREE.BoxGeometry(0.20, 0.10, 0.14),
    matSkin(skinColor),
  );
  bigJaw.position.set(0, 1.55, -0.02);
  group.add(bigJaw);

  // Golden blonde hair
  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(0.155, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.47),
    mat(color),
  );
  hair.position.set(0, 1.76, 0);
  group.add(hair);
  // Hair swoosh to side
  const swoosh = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 6), mat(color));
  swoosh.position.set(0.08, 1.80, -0.04);
  group.add(swoosh);

  // White teeth grin
  const teeth = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.025, 0.02), mat(0xffffff));
  teeth.position.set(0, 1.53, -0.12);
  group.add(teeth);

  // Gold chain necklace — signature crypto flex
  const chain = new THREE.Mesh(
    new THREE.TorusGeometry(0.13, 0.008, 8, 16),
    mat(color, { roughness: 0.15, metalness: 0.85 }),
  );
  chain.position.set(0, 1.43, -0.02);
  chain.rotation.x = Math.PI / 2 + 0.25;
  group.add(chain);

  // Chain pendant (crypto coin)
  const pendant = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.005, 10),
    mat(color, { roughness: 0.15, metalness: 0.85 }),
  );
  pendant.position.set(0, 1.36, -0.14);
  pendant.rotation.x = Math.PI / 2;
  group.add(pendant);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #13 — The NPC: Generic featureless character (intentionally bland) */
/* ------------------------------------------------------------------ */

export function buildTheNPC(color: number): THREE.Group {
  const group = new THREE.Group();

  // Uses humanoid base but deliberately plain — no face, no detail
  const body = buildHumanoid({
    bodyColor: color,
    headColor: color,
    torsoScaleX: 1.0,
    torsoScaleZ: 0.7,
    armColor: color,
    legColor: darken(color, 0.85),
    bootColor: darken(color, 0.6),
    handColor: darken(color, 0.75),
    showFace: false, // NO FACE — blank NPC
  });
  group.add(body);

  // Featureless smooth head overlay (covers the jaw from humanoid)
  const blankFace = new THREE.Mesh(new THREE.SphereGeometry(0.135, SEG, SEG), mat(color));
  blankFace.position.set(0, 1.63, -0.02);
  blankFace.scale.set(0.5, 0.5, 0.5);
  group.add(blankFace);

  // Simple "?" on chest — what even is this character?
  const qMark = new THREE.Mesh(
    new THREE.TorusGeometry(0.025, 0.006, 4, 8, Math.PI * 1.5),
    mat(darken(color, 0.6)),
  );
  qMark.position.set(0, 1.24, -0.19);
  qMark.rotation.x = Math.PI / 2;
  group.add(qMark);
  const qDot = new THREE.Mesh(new THREE.SphereGeometry(0.008, 4, 4), mat(darken(color, 0.6)));
  qDot.position.set(0, 1.18, -0.19);
  group.add(qDot);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #14 — Crypto Anon: Hooded figure with Guy Fawkes mask              */
/* ------------------------------------------------------------------ */

export function buildCryptoAnon(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = 0x0a0a0a; // black suit
  const maskColor = 0xf0e8d0;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: maskColor,
    armColor: suitColor,
    legColor: suitColor,
    bootColor: 0x0a0a0a,
    handColor: suitColor,
    showFace: false, // Guy Fawkes mask replaces face
  });
  group.add(body);

  // Hood — black
  const hood = new THREE.Mesh(
    new THREE.ConeGeometry(0.21, 0.28, SEG),
    mat(0x0a0a0a),
  );
  hood.position.set(0, 1.84, 0.02);
  group.add(hood);

  // Guy Fawkes mask features:
  // Pointed chin
  const chin = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.07, 6), mat(maskColor));
  chin.position.set(0, 1.50, -0.11);
  chin.rotation.x = Math.PI;
  group.add(chin);

  // Arched eyebrows
  for (const side of [-1, 1]) {
    const brow = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.01, 0.012), mat(0x1a1a1a));
    brow.position.set(side * 0.042, 1.72, -0.132);
    brow.rotation.z = side * -0.25;
    group.add(brow);
  }

  // Dark eye holes
  for (const side of [-0.042, 0.042]) {
    const eyeHole = new THREE.Mesh(new THREE.SphereGeometry(0.016, 8, 8), mat(0x0a0a0a));
    eyeHole.position.set(side, 1.68, -0.14);
    group.add(eyeHole);
  }

  // Thin mustache
  const mustache = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.008, 0.012), mat(0x1a1a1a));
  mustache.position.set(0, 1.60, -0.14);
  group.add(mustache);
  // Mustache curls
  for (const side of [-1, 1]) {
    const curl = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.001, 0.025, 6), mat(0x1a1a1a));
    curl.position.set(side * 0.04, 1.60, -0.14);
    curl.rotation.z = side * -0.8;
    group.add(curl);
  }

  // Rosy cheek marks on mask
  for (const side of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.012, 6, 6), mat(0xdd8888));
    cheek.position.set(side * 0.06, 1.62, -0.13);
    cheek.scale.set(1.2, 0.8, 0.3);
    group.add(cheek);
  }

  // Smile curve
  const smile = new THREE.Mesh(
    new THREE.TorusGeometry(0.028, 0.004, 4, 10, Math.PI),
    mat(0xaa3333),
  );
  smile.position.set(0, 1.56, -0.13);
  smile.rotation.x = Math.PI;
  group.add(smile);

  // Matrix green code lines on body — hacker aesthetic
  for (let i = 0; i < 10; i++) {
    const rng = Math.sin(i * 73.156) * 0.5 + 0.5;
    const codeBit = new THREE.Mesh(
      new THREE.BoxGeometry(0.012, 0.018 + rng * 0.025, 0.008),
      matEmissive(color, 0.8),
    );
    codeBit.position.set(
      -0.10 + (i % 5) * 0.05,
      1.02 + Math.floor(i / 5) * 0.10 + rng * 0.03,
      -0.17,
    );
    group.add(codeBit);
  }

  // Gloves detail
  for (const side of [-1, 1]) {
    const gloveRim = new THREE.Mesh(
      new THREE.CylinderGeometry(0.038, 0.040, 0.02, 8),
      mat(0x222222),
    );
    gloveRim.position.set(side * 0.28, 0.95, 0);
    group.add(gloveRim);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #15 — Pepe: Human in frog-themed outfit with Pepe mask/helmet      */
/* ------------------------------------------------------------------ */

export function buildPepe(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = darken(color, 0.6);
  const skinColor = 0xc8b098;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.15,
    armColor: suitColor,
    legColor: darken(suitColor, 0.85),
    bootColor: darken(color, 0.4),
    handColor: color, // green gloves
    showFace: false, // Pepe helmet covers face
  });
  group.add(body);

  // PEPE FROG HELMET — oversized round green helmet
  const pepeHead = new THREE.Mesh(new THREE.SphereGeometry(0.22, SEG, SEG), mat(color));
  pepeHead.position.set(0, 1.72, 0);
  pepeHead.scale.set(1.1, 0.92, 0.95);
  group.add(pepeHead);

  // HUGE BULGING EYES on helmet — white spheres with black pupils
  for (const side of [-1, 1]) {
    const eyeWhite = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 10), mat(0xffffff));
    eyeWhite.position.set(side * 0.12, 1.88, -0.06);
    group.add(eyeWhite);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 8), mat(0x0a0a0a));
    pupil.position.set(side * 0.12, 1.88, -0.12);
    group.add(pupil);
  }

  // Wide frog mouth on helmet
  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.035, 0.10),
    mat(0xcc4444),
  );
  mouth.position.set(0, 1.60, -0.18);
  group.add(mouth);

  // Lighter belly patch on suit
  const belly = new THREE.Mesh(
    new THREE.SphereGeometry(0.10, 8, 8),
    mat(lighten(color, 1.4)),
  );
  belly.position.set(0, 1.12, -0.14);
  belly.scale.set(1, 1.2, 0.3);
  group.add(belly);

  // Webbed green glove details
  for (const side of [-1, 1]) {
    const webbing = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.04, 0.01),
      mat(lighten(color, 1.2)),
    );
    webbing.position.set(side * 0.28, 0.78, 0);
    group.add(webbing);
  }

  // "Feels good man" text area on back (just a green patch)
  const backPatch = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.08, 0.01),
    mat(lighten(color, 1.3)),
  );
  backPatch.position.set(0, 1.20, 0.18);
  group.add(backPatch);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #16 — The Shiba: Human in Shiba Inu themed outfit with dog helmet  */
/* ------------------------------------------------------------------ */

export function buildTheShiba(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = darken(color, 0.5);
  const skinColor = 0xd4b896;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.1,
    armColor: suitColor,
    legColor: darken(suitColor, 0.85),
    bootColor: darken(color, 0.4),
    handColor: color, // golden gloves
    showFace: false, // Shiba helmet covers face
    eyeColor: 0x553311,
  });
  group.add(body);

  // SHIBA DOG HELMET — round golden head
  const shibaHead = new THREE.Mesh(new THREE.SphereGeometry(0.19, SEG, SEG), mat(color));
  shibaHead.position.set(0, 1.72, 0);
  shibaHead.scale.set(1.08, 1.0, 0.95);
  group.add(shibaHead);

  // White cheek patches
  for (const side of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 8), mat(0xf5f0e0));
    cheek.position.set(side * 0.10, 1.68, -0.10);
    cheek.scale.set(1.0, 0.8, 0.5);
    group.add(cheek);
  }

  // Pointed DOG EARS on helmet
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.13, 8), mat(color));
    ear.position.set(side * 0.12, 1.92, 0.02);
    ear.rotation.z = side * 0.18;
    group.add(ear);
    const innerEar = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.07, 6), mat(0xffaaaa));
    innerEar.position.set(side * 0.12, 1.91, -0.01);
    innerEar.rotation.z = side * 0.18;
    group.add(innerEar);
  }

  // Snout protruding from helmet
  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.06, 0.09), mat(lighten(color, 1.2)));
  snout.position.set(0, 1.65, -0.18);
  group.add(snout);
  // Black nose
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.022, 8, 8), mat(0x0a0a0a));
  nose.position.set(0, 1.67, -0.23);
  group.add(nose);

  // Dark expressive eyes on helmet
  for (const side of [-0.06, 0.06]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.022, 8, 8), mat(0x1a0a00));
    eye.position.set(side, 1.74, -0.16);
    group.add(eye);
    const shine = new THREE.Mesh(new THREE.SphereGeometry(0.007, 4, 4), mat(0xffffff));
    shine.position.set(side - 0.008, 1.745, -0.175);
    group.add(shine);
  }

  // White chest patch on suit
  const chestPatch = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 8), mat(0xf5f0e0));
  chestPatch.position.set(0, 1.16, -0.14);
  chestPatch.scale.set(0.8, 1.2, 0.3);
  group.add(chestPatch);

  // Curved tail attached to belt (cosmetic)
  for (let i = 0; i < 5; i++) {
    const t = i / 4;
    const tail = new THREE.Mesh(
      new THREE.SphereGeometry(0.025 - t * 0.006, 6, 6),
      mat(color),
    );
    tail.position.set(
      0,
      0.90 + Math.sin(t * Math.PI * 0.7) * 0.18,
      0.18 + t * 0.10,
    );
    group.add(tail);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #17 — GigaChad: Ultra-masculine proportioned human                 */
/* ------------------------------------------------------------------ */

export function buildGigaChad(color: number): THREE.Group {
  const group = new THREE.Group();
  const darkColor = darken(color, 0.7);
  const skinColor = 0xc0a888;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.65,
    torsoScaleZ: 0.85,
    torsoWidth: 0.22,
    torsoHeight: 0.38,
    armColor: color,
    legColor: darkColor,
    bootColor: 0x111111,
    handColor: skinColor,
    shoulderWidth: 0.40,
    muscular: true,
    eyeColor: 0x445566,
  });
  group.add(body);

  // Thick neck overlay (extra thick)
  const thickNeck = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.08, SEG), matSkin(skinColor));
  thickNeck.position.set(0, 1.50, 0);
  group.add(thickNeck);

  // MASSIVE SQUARE JAW — bigger than Chad
  const megaJaw = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.12, 0.16),
    matSkin(skinColor),
  );
  megaJaw.position.set(0, 1.55, -0.01);
  group.add(megaJaw);

  // Stern brow ridge
  const brow = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.025, 0.05), mat(darken(skinColor, 0.7)));
  brow.position.set(0, 1.74, -0.11);
  group.add(brow);

  // Dark buzz-cut hair
  addBuzzCut(group, 0x222222, 1.66, 0.14);

  // Chiseled pec definition (extra prominent)
  for (const side of [-1, 1]) {
    const pecPlate = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.09, 0.04), mat(lighten(color, 1.12)));
    pecPlate.position.set(side * 0.10, 1.28, -0.16);
    pecPlate.rotation.y = side * 0.08;
    group.add(pecPlate);
  }

  // Extra-wide shoulders (pad overlay)
  for (const side of [-1, 1]) {
    const shoulderCap = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), mat(color));
    shoulderCap.position.set(side * 0.36, 1.42, 0);
    shoulderCap.scale.set(1.3, 0.6, 1.0);
    group.add(shoulderCap);
  }

  // Massive forearms (bulging)
  for (const side of [-1, 1]) {
    const bigForearm = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8), matSkin(skinColor));
    bigForearm.position.set(side * 0.40, 1.0, 0);
    bigForearm.scale.set(0.8, 1.2, 0.8);
    group.add(bigForearm);
  }

  // Taller than normal
  group.scale.set(1.0, 1.06, 1.0);

  enableShadows(group);
  return group;
}
